import ListCreateButton from 'components/buttons/ListCreateButton';
import Loader from 'components/Loader';
import { useModal } from 'components/modals/useModal';
import PageHeader from 'components/PageHeader';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useCallback } from 'react';
import {
    useAddPricelistMutation,
    useDeletePricelistMutation,
    useLazyGetPricelistsWithFiltersQuery,
    useUpdatePricelistMutation,
} from 'store/api/pricelists';
import { CalculatedPricelist, PricelistDto } from 'types/pricelists';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { EditPricelistModal } from './modal/EditPricelistModal';
import PricelistsTable from './PricelistsTable';

// ==============================|| PRICELIST LIST PAGE ||============================== //

const PricelistsPage = (): JSX.Element => {
    const [fetchData, { data, isFetching }] = useLazyGetPricelistsWithFiltersQuery();

    const { openModal, closeModal } = useModal();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['CURRENCY', 'JOB_ROLE_GROUP_TYPE', 'SCOPE_TYPE'],
    });

    const [addPricelist, { isLoading: isAddPricelistLoading }] = useAddPricelistMutation();
    const [updatePricelist, { isLoading: isUpdatePricelistLoading }] = useUpdatePricelistMutation();
    const [, { isLoading: isDeletePricelistLoading }] = useDeletePricelistMutation();

    const onFormSumbit = useCallback(
        async (formData: PricelistDto, entityId?: string): Promise<void> => {
            try {
                if (entityId) {
                    await updatePricelist({ entityId, ...formData }).unwrap();
                } else {
                    await addPricelist({ ...formData }).unwrap();
                }
                closeModal();
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [updatePricelist, addPricelist, closeModal]
    );

    const openEditPricelistModal = useCallback(
        (pricelist?: CalculatedPricelist): void => {
            const scopeType = pricelist?.scopeTypeCode || 'TENANT';

            if (scopeType !== 'TENANT' && scopeType !== 'COMPANY') {
                return;
            }

            const artModalStyle = { width: 800 };
            openModal(
                <EditPricelistModal
                    scopeType={scopeType}
                    entityId={pricelist?.id}
                    onFormSumbit={onFormSumbit}
                    onClose={closeModal}
                    classifiers={classifiers}
                    // deleteButton={getDeleteButton(pricelist?.id)}
                />,
                closeModal,
                artModalStyle
            );
        },
        [openModal, onFormSumbit, closeModal, classifiers]
    );

    if (isAddPricelistLoading || isUpdatePricelistLoading || isDeletePricelistLoading || isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <PageHeader
                    hostsList
                    titleId={'pricelists.pricelists'}
                    showBreadcrumb
                    buttons={
                        <ListCreateButton
                            permissions={['CREATE.PRICELISTS']}
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => openEditPricelistModal()}
                            labelId="pricelists.add-new-pricelist"
                            newLabelId="new-entity.price-list"
                        />
                    }
                />
                <PricelistsTable
                    data={data}
                    fetchData={fetchData}
                    openEditModal={openEditPricelistModal}
                    isFetching={isFetching}
                />
            </div>
        </PaddedLayout>
    );
};

export default PricelistsPage;
